import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Button, IconButton } from './UI';
import CartDrawer from './CartDrawer';

/* ── Styled ─────────────────────────────────────────────────── */
const HeaderWrap = styled.header`
  background: #fff; border-bottom: 1px solid var(--gray-100);
  height: var(--header-height); position: sticky; top: 0; z-index: 100;
  box-shadow: var(--shadow-sm);
`;
const Inner = styled.div`
  max-width: 1280px; margin: 0 auto; padding: 0 20px;
  display: flex; align-items: center; justify-content: space-between; height: 100%;
`;
const Logo = styled(Link)`
  font-family: var(--font-display); font-size: 1.5rem; font-weight: 700;
  color: var(--primary); display: flex; align-items: center; gap: 2px;
  span { color: var(--primary-light); }
`;
const Nav = styled.nav`
  display: flex; align-items: center; gap: 32px;
  @media(max-width:768px){ display: none; }
`;
const NavLink = styled(Link)`
  font-size: 0.9rem; font-weight: 500; color: var(--gray-600);
  transition: var(--transition); position: relative;
  &:hover, &.active { color: var(--primary); }
  &.active::after { content: ''; position: absolute; bottom: -4px; left: 0; right: 0; height: 2px; background: var(--primary); border-radius: 2px; }
`;
const Actions = styled.div` display: flex; align-items: center; gap: 10px; `;
const CartBtn = styled.button`
  position: relative; width: 40px; height: 40px; border-radius: 50%; border: 1.5px solid var(--gray-200);
  background: #fff; display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 1.1rem; transition: var(--transition);
  &:hover { border-color: var(--primary); }
`;
const CartBadge = styled.span`
  position: absolute; top: -5px; right: -5px; width: 18px; height: 18px;
  background: var(--primary); color: #fff; border-radius: 50%;
  font-size: 0.65rem; font-weight: 700; display: flex; align-items: center; justify-content: center;
`;
const UserMenu = styled.div` position: relative; `;
const UserDropdown = styled.div`
  position: absolute; top: calc(100% + 8px); right: 0; background: #fff;
  border-radius: var(--radius-lg); box-shadow: var(--shadow-xl);
  border: 1px solid var(--gray-100); min-width: 180px; overflow: hidden; z-index: 200;
`;
const DropItem = styled(Link)`
  display: flex; align-items: center; gap: 8px; padding: 10px 16px;
  font-size: 0.88rem; color: var(--gray-700); transition: var(--transition);
  &:hover { background: var(--gray-50); color: var(--primary); }
`;
const DropDivider = styled.div` border-top: 1px solid var(--gray-100); margin: 4px 0; `;

export function Header() {
  const { user, isLoggedIn, logout } = useAuth();
  const { count, setOpen } = useCart();
  const [menuOpen, setMenuOpen] = useState(false);
  const [mobileNav, setMobileNav] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const isActive = path => location.pathname === path ? 'active' : '';

  const handleLogout = () => { logout(); navigate('/'); setMenuOpen(false); };

  const dashboardLink = () => {
    if (user?.role === 'admin') return '/admin';
    if (user?.role === 'vendor') return '/vendor';
    return '/account';
  };

  return (
    <>
      <HeaderWrap>
        <Inner>
          <Logo to="/">Ameria<span>vis</span></Logo>

          <Nav>
            <NavLink to="/" className={isActive('/')}>Home</NavLink>
            <NavLink to="/shop" className={isActive('/shop')}>Shop</NavLink>
            <NavLink to="/shop?category=">Categories</NavLink>
            <NavLink to="/about">About</NavLink>
          </Nav>

          <Actions>
            <CartBtn onClick={() => setOpen(true)} title="Cart">
              🛒
              {count > 0 && <CartBadge>{count}</CartBadge>}
            </CartBtn>

            {isLoggedIn && (
              <IconButton onClick={() => navigate('/account/wishlist')} title="Wishlist">♡</IconButton>
            )}

            {isLoggedIn ? (
              <UserMenu>
                <Button $variant="outline" $size="sm" onClick={() => setMenuOpen(p => !p)}
                  style={{ gap:6 }}>
                  {user.name.split(' ')[0]} ▾
                </Button>
                {menuOpen && (
                  <UserDropdown>
                    <DropItem to={dashboardLink()} onClick={() => setMenuOpen(false)}>
                      {user.role === 'admin' ? '🔐 Admin Panel' : user.role === 'vendor' ? '🏪 Vendor Panel' : '👤 My Account'}
                    </DropItem>
                    {user.role === 'customer' && <>
                      <DropItem to="/account/orders" onClick={() => setMenuOpen(false)}>📦 My Orders</DropItem>
                      <DropItem to="/account/wishlist" onClick={() => setMenuOpen(false)}>♡ Wishlist</DropItem>
                      <DropItem to="/account/profile" onClick={() => setMenuOpen(false)}>⚙️ Profile</DropItem>
                    </>}
                    <DropDivider />
                    <button onClick={handleLogout} style={{ display:'flex', alignItems:'center', gap:8, padding:'10px 16px', fontSize:'0.88rem', color:'var(--error)', background:'none', border:'none', cursor:'pointer', width:'100%' }}>
                      🚪 Logout
                    </button>
                  </UserDropdown>
                )}
              </UserMenu>
            ) : (
              <div style={{ display:'flex', gap:8 }}>
                <Button $variant="secondary" $size="sm" onClick={() => navigate('/login')}>Login</Button>
                <Button $size="sm" onClick={() => navigate('/register')}>Register</Button>
              </div>
            )}
          </Actions>
        </Inner>
      </HeaderWrap>
      <CartDrawer />
    </>
  );
}

/* ── Footer ─────────────────────────────────────────────────── */
export function Footer() {
  return (
    <footer style={{ background:'var(--gray-900)', color:'rgba(255,255,255,.7)', padding:'60px 0 24px' }}>
      <div style={{ maxWidth:1280, margin:'0 auto', padding:'0 20px' }}>
        <div style={{ display:'grid', gridTemplateColumns:'2fr 1fr 1fr 1fr', gap:40, marginBottom:40 }}>
          <div>
            <div style={{ fontFamily:'var(--font-display)', fontSize:'1.4rem', fontWeight:700, color:'var(--primary-light)' }}>Ameriavis</div>
            <p style={{ fontSize:'0.88rem', lineHeight:1.7, marginTop:12, maxWidth:280 }}>Your trusted online shopping destination. Quality products, fast delivery, and exceptional service.</p>
          </div>
          {[
            { title:'Quick Links', links:[['Home','/'],['Shop','/shop'],['My Orders','/account/orders'],['Wishlist','/account/wishlist']] },
            { title:'Help', links:[['FAQ','#'],['Shipping Policy','#'],['Return Policy','#'],['Contact Us','#']] },
            { title:'Account', links:[['Login','/login'],['Register','/register'],['My Profile','/account/profile'],['Order History','/account/orders']] },
          ].map(col => (
            <div key={col.title}>
              <h4 style={{ color:'#fff', fontSize:'0.92rem', fontWeight:600, marginBottom:14, fontFamily:'var(--font-body)' }}>{col.title}</h4>
              {col.links.map(([label, href]) => <Link key={label} to={href} style={{ display:'block', fontSize:'0.85rem', marginBottom:8, color:'rgba(255,255,255,.6)', transition:'var(--transition)' }}>{label}</Link>)}
            </div>
          ))}
        </div>
        <div style={{ borderTop:'1px solid rgba(255,255,255,.08)', paddingTop:24, display:'flex', alignItems:'center', justifyContent:'space-between', fontSize:'0.82rem', flexWrap:'wrap', gap:12 }}>
          <span>© {new Date().getFullYear()} Ameriavis. Powered by <a href="https://ameriavistech.com" style={{ color:'var(--primary-light)' }}>Ameriavis Tech</a></span>
          <span>PayPal Accepted · SSL Secured</span>
        </div>
      </div>
    </footer>
  );
}

/* ── Page Layout ─────────────────────────────────────────────── */
export function PageLayout({ children }) {
  return (
    <div style={{ display:'flex', flexDirection:'column', minHeight:'100vh' }}>
      <Header />
      <main style={{ flex:1 }}>{children}</main>
      <Footer />
    </div>
  );
}

/* ── Container ───────────────────────────────────────────────── */
export const Container = styled.div`
  max-width: 1280px; margin: 0 auto; padding: 0 20px;
`;
